/**
 * @description
 */

import './sectionNumber.scss';

import React, { Component } from 'react';
import { classSet } from '@umu/umu-dom';
import { components } from 'common';
const { If, NumericInput } = components;

import {
    ESectionValid,
    ESectionType,
    ISectionOptionBaseInfo,
    ISectionBaseExtend,
    ISectionBaseSetup,
    ISectionBaseInfo,
    ISectionBaseResult,
} from '@umu/umu-types';

import RangeSlider from '../../../../../services/rangeSlider';

export interface IProps<IType, IOption, IExtend> {
    sectionInfo: ISectionBaseInfo<IType, IOption, IExtend>;
    isDisabled?: boolean;
    step?: number;
    showInput?: boolean;
    onValueUpdate(value: ISectionBaseResult<any>, isValid: ESectionValid): void;
}

export interface IState {
    percent: number;
    result: ISectionBaseResult<any>;
    isValid: ESectionValid;
    changed: boolean;
}

// tslint:disable-next-line:max-line-length
export default abstract class SectionNumber<
    IP extends IProps<ESectionType, ISectionOptionBaseInfo, ISectionBaseExtend<ISectionBaseSetup>>,
    IS extends IState,
> extends Component<IP, IS> {
    private slider: RangeSlider | undefined;
    private parentRef: React.RefObject<HTMLDivElement> = React.createRef();

    // tslint:disable-next-line:max-line-length
    constructor(props: IP, state: IS) {
        super(props, state);

        const userValue = props.sectionInfo.userValue || []; // 默认值不会作为用户默认的选择值，所以这里不加 defaultValue
        const result = {
            userValue,
        };
        this.state = Object.assign({}, this.state, {
            percent:
                this.calculateUserValuePercent(
                    Number(
                        userValue[0] ||
                            props.sectionInfo.extend.setup.number_default ||
                            props.sectionInfo.extend.setup.number_max,
                    ),
                ) || 0,
            result,
            isValid: this.validSection(props.sectionInfo, result),
            changed: false,
        });
    }

    // tslint:disable-next-line:max-line-length
    public abstract validSection(
        section: ISectionBaseInfo<any, ISectionOptionBaseInfo, ISectionBaseExtend<ISectionBaseSetup>>,
        result: ISectionBaseResult<any>,
    ): ESectionValid;

    // tslint:disable-next-line:max-line-length
    public UNSAFE_componentWillReceiveProps(
        nextProps: IProps<ESectionType, ISectionOptionBaseInfo, ISectionBaseExtend<ISectionBaseSetup>>,
    ) {
        if (this.props.isDisabled && !nextProps.isDisabled) {
            this.initSlider();
        }
    }

    public componentDidMount() {
        if (!this.props.isDisabled) {
            this.initSlider();
        }
    }

    public componentWillUnmount() {
        if (this.slider) {
            this.slider.destroy();
        }
    }

    public initSlider() {
        this.slider = new RangeSlider({
            parentEl: this.parentRef,
            buttonEl: '.j_number-slider-bar .j_button',
            onRangeChange: (percent) => {
                this.onRangeChange(percent);
            },
        });
        this.slider.run();
    }

    /**
     * @override 获取用户操作的最终值
     */
    public getUserValue() {
        return this.state.result;
    }

    public getStep() {
        let { step = 1 } = this.props;
        if (Number(step) === 0) {
            step = 1;
        }
        return step;
    }

    /**
     * @description 获取滑块的集合
     */
    public getSteps() {
        const { setup } = this.props.sectionInfo.extend;
        const step = this.getStep();
        const min = step < 1 ? Number(setup.number_min).toFixed(1) : Number(setup.number_min);
        const max = Number(setup.number_max);
        const stopCount = (max - Number(min)) / step;
        const result = [];
        for (let i = 1; i <= stopCount; i++) {
            const stepWidh = step < 1 ? +(i * step).toFixed(1) : Math.round(i * step);
            let val: string | number = stepWidh + Number(min);
            val = step < 1 ? val.toFixed(1) : val;
            result.push(val);
        }
        result.unshift(min);
        return result;
    }

    public calculateUserValue = (percent: number = 0) => {
        const { setup } = this.props.sectionInfo.extend;
        // 这里需要根据step 取值
        const min = Number(setup.number_min);
        const max = Number(setup.number_max);
        let steps = this.getSteps();
        steps = steps.filter((step) => {
            const current = percent * (max - min) + min;
            return Number(step) <= current;
        });
        return steps[steps.length - 1] || min;
    };

    public calculateUserValuePercent = (userValue: number = 0) => {
        const { setup } = this.props.sectionInfo.extend;
        const min = Number(setup.number_min);
        const max = Number(setup.number_max);
        return (userValue - min) / (max - min);
    };
    public onRangeChange = (percent: number = 0) => {
        percent = percent > 0.95 ? 1 : percent;
        const userValue = this.calculateUserValue(percent);
        percent = this.calculateUserValuePercent(+userValue);
        this.updateValue(userValue, percent);
    };
    public updateValue = (userValue: number | string, percent: number) => {
        const newUserValue = userValue + '';
        if (newUserValue === this.state.result.userValue[0]) {
            return;
        }

        const result = {
            userValue: [newUserValue],
        };
        const isValid = this.validSection(this.props.sectionInfo, result);
        this.setState(
            {
                percent,
                result,
                isValid,
                changed: true,
            },
            () => {
                this.props.onValueUpdate(this.getUserValue(), isValid);
            },
        );
    };

    public onInpuntNumberChange = (userValue: string | number) => {
        const step = this.getStep();
        if (userValue === '') {
            this.updateValue('', 0);
            return;
        }
        const { setup } = this.props.sectionInfo.extend;
        const min = Number(setup.number_min);
        const max = Number(setup.number_max);
        // 当可以输入小数时，限制用户输入超过一位的小数
        if (step === 1) {
            userValue = Number(userValue);
        }
        // 当可以输入小数时，限制用户输入超过一位的小数
        if (step < 1 && userValue.toString().indexOf('.') > -1) {
            const index = userValue.toString().indexOf('.');
            if (userValue.toString().slice(index).length >= 2) {
                userValue = parseFloat(userValue.toString()).toFixed(1);
            }
        }
        if (Number(userValue) < min) {
            return;
        }
        if (Number(userValue) > max) {
            return;
        }
        let steps = this.getSteps();
        steps = steps.filter((stepValue) => {
            return Number(stepValue) === Number(userValue);
        });
        if (steps.length === 0) {
            return;
        }
        const percent = this.calculateUserValuePercent(Number(userValue));
        this.updateValue(userValue, percent);
    };
    public onInputNumberBlur = (userValue: string | number) => {
        const step = this.getStep();
        if (!userValue) {
            return;
        }
        if (step < 1) {
            const percent = this.calculateUserValuePercent(Number(userValue));
            this.updateValue((+userValue).toFixed(1), percent);
        }
    };

    public render() {
        const { sectionInfo, isDisabled, showInput = false } = this.props;
        const { schemeIndex = 1, extend } = sectionInfo;
        const { setup } = extend;
        const min = Number(setup.number_min);
        const max = Number(setup.number_max);
        const step = this.getStep();
        const minDesc = setup.number_min_tag;
        const maxDesc = setup.number_max_tag;

        const { percent, result } = this.state;
        const changed = +result.userValue > min

        return (
            <div
                className={classSet({
                    'section-item-body section-number show-input': showInput,
                    'section-item-body section-number': !showInput,
                })}
            >
                <If condition={!showInput}>
                    <div className={`range-value slider-scheme-txt-${schemeIndex}`}>{result.userValue}</div>
                </If>
                <If condition={showInput}>
                    <div className="range-value-input input">
                        <NumericInput
                            onBlur={(val) => this.onInputNumberBlur(val)}
                            isDisabled={isDisabled}
                            value={result.userValue}
                            onChange={(val) => this.onInpuntNumberChange(val)}
                        />
                    </div>
                </If>
                <div className="slider-axises">
                    <div className="slider-axis min-label">{step < 1 ? min.toFixed(1) : min}</div>
                    <div className="slider-axis max-label">{step < 1 ? max.toFixed(1) : max}</div>
                </div>
                <div className={`slider-bar j_number-slider-bar ${isDisabled ? 'disabled' : ''}`}>
                    <div className="slider-bar-wrap j_parent" ref={this.parentRef}>
                        <div className="slider-btn-groove">
                            <div
                                className={`slider-btn j_button slider-scheme-txt-${schemeIndex}`}
                                style={{
                                    left: `${percent * 100}%`,
                                }}
                            >
                                {result.userValue}
                            </div>
                        </div>
                        <div
                            className={`slider-value slider-scheme-bg-${schemeIndex} ${
                                changed ? '' : 'slider-default'
                            }`}
                            style={{
                                width: `${percent * 100}%`,
                            }}
                        ></div>
                    </div>
                </div>
                <div className="slider-legends">
                    <div className="slider-legend min-label">{minDesc}</div>
                    <div className="slider-legend max-label">{maxDesc}</div>
                </div>
            </div>
        );
    }
}
